import * as React from 'react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useQueryParam } from '../../hooks/useQueryParam';
import { Alert, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AutocompleteTextField } from '../../components/Forms/fields';
import Typography from '../../components/Typography';
import { getSurveyAnalyticsAsync } from '../../ajax/requests';
import { DateRangeSelectorState } from '../../components/DateRangeSelector';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { ProfessionalData } from '../../models';
import { useGeneralSettings } from '../../store/settings/hooks';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getSurveys, isSurveysLoading } from '../../store/settings/selectors';
import { loadSurveys } from '../../store/settings/actions';
import {
    SurveyAnalyticsData,
    SurveyAnalyticsFieldResult,
    SurveyAnalyticsResponse,
    SurveyFieldType,
} from '../../shared/surveys';
import moment from 'moment';
import { DimensionMetrics } from './DimensionMetrics';

interface SurveyRef {
    id: string;
    name: string;
}

interface NamedFilterOption {
    id: string;
    name: string;
}

interface AnalyticsSurveysProps {
    window: DateRangeSelectorState;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--spacer-S);
`;

const SummaryCard = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    border-radius: 12px;
    border: var(--border-thin) solid var(--appLigthGrey);
    background: var(--bg-light);
`;

const SummaryLabel = styled.span`
    color: var(--text-muted);
    font-size: 0.9rem;
`;

const SummaryCount = styled.span`
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--brand-secondary);
`;

const LoadingContainer = styled.div`
    text-align: center;
    padding: var(--spacer-M);
`;

const FieldCard = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: var(--border-thick) solid var(--brand-secondary);
    border-radius: 8px;
    background: white;
    overflow: hidden;
`;

const FieldCardHeader = styled.div`
    padding: var(--spacer-S) var(--spacer-M);
    background: var(--bg-light);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
    flex-wrap: wrap;
`;

const FieldLabel = styled.div`
    font-weight: 700;
    color: var(--text-body);
    font-size: 1rem;
`;

const AverageBadge = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
`;

const AverageValue = styled.span`
    font-size: 1.6rem;
    font-weight: 700;
    color: var(--brand-secondary);
    line-height: 1;
`;

const AverageLabel = styled.span`
    font-size: 0.8rem;
    color: var(--text-muted);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const FieldCardBody = styled.div`
    padding: var(--spacer-S) var(--spacer-M);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const BreakdownGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--spacer-S);
`;

const OptionBarRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const OptionLabel = styled.span`
    flex: 0 0 120px;
    font-size: 0.875rem;
    color: var(--text-body);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const OptionBarTrack = styled.div`
    flex: 1;
    height: 8px;
    border-radius: 999px;
    background: var(--appLigthGrey);
    overflow: hidden;
`;

const OptionBarFill = styled.div<{ $pct: number }>`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    border-radius: 999px;
    background: var(--brand-primary);
    transition: width 0.5s ease;
`;

const OptionCount = styled.span`
    flex: 0 0 2.5rem;
    text-align: right;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--text-muted);
`;

const TextResponseList = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
    max-height: 280px;
    overflow-y: auto;
`;

const TextResponseItem = styled.div`
    padding: var(--spacer-XS) var(--spacer-S);
    border-radius: 8px;
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    color: var(--text-body);
    font-size: 0.875rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
`;

const ResponsesSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    margin-top: var(--spacer-S);
`;

const ResponsesSectionTitle = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: var(--text-body);
`;

const ResponseCard = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 8px;
    background: white;
    overflow: hidden;
`;

const ResponseHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
    flex-wrap: wrap;
    padding: var(--spacer-XS) var(--spacer-S);
    background: var(--bg-light);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    font-size: 0.85rem;
    color: var(--text-muted);
`;

const ResponseHeaderTitle = styled.span`
    font-weight: 600;
    color: var(--text-body);
`;

const ResponseChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacer-XS);
`;

const ResponseChip = styled.span`
    padding: 1px var(--spacer-XS);
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 4px;
    font-size: 0.75rem;
    color: var(--text-muted);
`;

const ResponseBody = styled.div`
    padding: var(--spacer-S);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

const ResponseFieldRow = styled.div`
    display: grid;
    grid-template-columns: minmax(140px, 1fr) 2fr;
    gap: var(--spacer-S);
    font-size: 0.875rem;
    line-height: 1.4;
`;

const ResponseFieldLabel = styled.span`
    color: var(--text-muted);
`;

const ResponseFieldValue = styled.span`
    color: var(--text-body);
    white-space: pre-wrap;
    word-break: break-word;
`;

interface ResponseListProps {
    responses: SurveyAnalyticsResponse[];
    fieldLabels: Record<string, string>;
}

const ResponseList: FC<ResponseListProps> = ({ responses, fieldLabels }) => {
    const t = useTranslations();
    if (responses.length === 0) {
        return <Typography><i>{t(TK.surveyNoIndividualResponses)}</i></Typography>;
    }
    return (
        <>
            {responses.map(response => {
                const valueEntries = Object.entries(response.values ?? {});
                return (
                    <ResponseCard key={response.id}>
                        <ResponseHeader>
                            <ResponseHeaderTitle>
                                {moment(response.creationDate).format('YYYY-MM-DD HH:mm')}
                                {response.isAnonymous
                                    ? ` · ${t(TK.surveyResponseAnonymous)}`
                                    : response.clientName ? ` · ${response.clientName}` : ''}
                            </ResponseHeaderTitle>
                            <ResponseChips>
                                {response.professionalName && <ResponseChip>{response.professionalName}</ResponseChip>}
                                {response.serviceTypeName && <ResponseChip>{response.serviceTypeName}</ResponseChip>}
                                {response.roomName && <ResponseChip>{response.roomName}</ResponseChip>}
                                {response.branchName && <ResponseChip>{response.branchName}</ResponseChip>}
                            </ResponseChips>
                        </ResponseHeader>
                        <ResponseBody>
                            {valueEntries.length === 0
                                ? <Typography><i>{t(TK.surveyNoIndividualResponses)}</i></Typography>
                                : valueEntries.map(([key, value]) => (
                                    <ResponseFieldRow key={key}>
                                        <ResponseFieldLabel>{fieldLabels[key] ?? key}</ResponseFieldLabel>
                                        <ResponseFieldValue>{value}</ResponseFieldValue>
                                    </ResponseFieldRow>
                                ))
                            }
                        </ResponseBody>
                    </ResponseCard>
                );
            })}
        </>
    );
};

interface OptionCountsProps {
    optionCounts: Record<string, number>;
}

const OptionCounts: FC<OptionCountsProps> = ({ optionCounts }) => {
    const entries = Object.entries(optionCounts);
    if (entries.length === 0) return null;

    const maxCount = Math.max(...entries.map(([, count]) => count));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
            {entries.map(([option, count]) => (
                <OptionBarRow key={option}>
                    <OptionLabel title={option}>{option}</OptionLabel>
                    <OptionBarTrack>
                        <OptionBarFill $pct={maxCount > 0 ? (count / maxCount) * 100 : 0} />
                    </OptionBarTrack>
                    <OptionCount>{count}</OptionCount>
                </OptionBarRow>
            ))}
        </div>
    );
};

interface FieldCardItemProps {
    field: SurveyAnalyticsFieldResult;
}

const FieldCardItem: FC<FieldCardItemProps> = ({ field }) => {
    const t = useTranslations();

    const hasDimensions =
        field.averageByProfessional.length > 0 ||
        field.averageByServiceType.length > 0 ||
        field.averageByRoom.length > 0;

    return (
        <FieldCard>
            <FieldCardHeader>
                <FieldLabel>{field.label}</FieldLabel>
                {field.average != null && (
                    <AverageBadge>
                        <AverageValue>{field.average.toFixed(1)}</AverageValue>
                        <AverageLabel>{t(TK.average)}</AverageLabel>
                    </AverageBadge>
                )}
                {field.type === SurveyFieldType.MultiOptions && field.optionCounts && (
                    <AverageBadge>
                        <AverageValue>{Object.values(field.optionCounts).reduce((a, b) => a + b, 0)}</AverageValue>
                        <AverageLabel>{t(TK.total)}</AverageLabel>
                    </AverageBadge>
                )}
                {field.type === SurveyFieldType.MultilineText && field.textResponses && (
                    <AverageBadge>
                        <AverageValue>{field.textResponses.length}</AverageValue>
                        <AverageLabel>{t(TK.total)}</AverageLabel>
                    </AverageBadge>
                )}
            </FieldCardHeader>

            <FieldCardBody>
                {field.optionCounts && Object.keys(field.optionCounts).length > 0 && (
                    <OptionCounts optionCounts={field.optionCounts} />
                )}

                {field.textResponses && field.textResponses.length > 0 && (
                    <TextResponseList>
                        {field.textResponses.map((text, index) => (
                            <TextResponseItem key={index}>{text}</TextResponseItem>
                        ))}
                    </TextResponseList>
                )}

                {hasDimensions && (
                    <BreakdownGrid>
                        <DimensionMetrics title={t(TK.byProfessional)} metrics={field.averageByProfessional} />
                        <DimensionMetrics title={t(TK.services)} metrics={field.averageByServiceType} />
                        <DimensionMetrics title={t(TK.byRoom)} metrics={field.averageByRoom} />
                    </BreakdownGrid>
                )}
            </FieldCardBody>
        </FieldCard>
    );
};

export const AnalyticsSurveys: FC<AnalyticsSurveysProps> = ({ window }) => {
    const dispatch = useDispatch();
    const t = useTranslations();
    const generalSettings = useGeneralSettings();
    const professionals = useSelector(getEntitiesDataItems<ProfessionalData>('professionals'));
    const surveysData = useSelector(getSurveys) ?? [];
    const surveysLoading = useSelector(isSurveysLoading);

    const surveys = useMemo<SurveyRef[]>(
        () => surveysData.map(survey => ({ id: survey.id, name: survey.name })),
        [surveysData],
    );
    const [surveyIdParam, setSurveyIdParam] = useQueryParam<string>('surveyId', '');
    const surveyId = surveyIdParam || undefined;
    const setSurveyId = (id: string | undefined) => setSurveyIdParam(id ?? '');
    const [serviceType, setServiceType] = useState<string | undefined>(undefined);
    const [professionalId, setProfessionalId] = useState<string | undefined>(undefined);
    const [roomId, setRoomId] = useState<string | undefined>(undefined);
    const [analytics, setAnalytics] = useState<SurveyAnalyticsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!surveysData.length && !surveysLoading) {
            dispatch(loadSurveys());
        }
    }, [dispatch, surveysData.length, surveysLoading]);

    useEffect(() => {
        setLoading(true);
        setError(false);
        getSurveyAnalyticsAsync({
            surveyId,
            serviceType,
            professionalId,
            roomId,
            from: window.from,
            to: window.to,
        })
            .then(data => setAnalytics(data))
            .catch(() => { setAnalytics(null); setError(true); })
            .finally(() => setLoading(false));
    }, [surveyId, serviceType, professionalId, roomId, window.from, window.to]);

    const serviceTypeOptions = useMemo<NamedFilterOption[]>(
        () => Object.entries(generalSettings.services ?? {}).map(([id, service]) => ({
            id,
            name: service.name?.trim() || id,
        })),
        [generalSettings.services],
    );

    const roomOptions = useMemo(
        () => Object.keys(generalSettings.rooms ?? {}),
        [generalSettings.rooms],
    );

    const professionalOptions = useMemo(
        () => (professionals ?? [])
            .map(professional => ({ id: professional.id, name: professional.name }))
            .filter(professional => !!professional.name),
        [professionals],
    );

    const hasFieldResults = Boolean((analytics?.fields ?? []).length);

    const responseFieldLabels = useMemo<Record<string, string>>(() => {
        const labels: Record<string, string> = {};
        const selected = surveysData.find(s => s.id === surveyId);
        if (selected) {
            selected.fields.forEach((field, index) => {
                labels[String(index)] = field.label;
            });
        }
        (analytics?.fields ?? []).forEach(field => {
            if (!labels[field.key]) labels[field.key] = field.label;
        });
        return labels;
    }, [analytics, surveyId, surveysData]);

    return (
        <Container>
            <FiltersGrid>
                <AutocompleteTextField
                    label={t(TK.surveys)}
                    options={surveys.map(survey => survey.name)}
                    value={surveys.find(survey => survey.id === surveyId)?.name ?? undefined}
                    onValueChanged={value => {
                        const selected = surveys.find(survey => survey.name === value);
                        setSurveyId(selected?.id);
                    }}
                />

                <AutocompleteTextField
                    label={t(TK.services)}
                    options={serviceTypeOptions.map(service => service.name)}
                    value={serviceTypeOptions.find(service => service.id === serviceType)?.name ?? undefined}
                    onValueChanged={value => {
                        const selected = serviceTypeOptions.find(service => service.name === value);
                        setServiceType(selected?.id);
                    }}
                />

                <AutocompleteTextField
                    label={t(TK.byProfessional)}
                    options={professionalOptions.map(professional => professional.name)}
                    value={professionalOptions.find(professional => professional.id === professionalId)?.name ?? undefined}
                    onValueChanged={value => {
                        const selected = professionalOptions.find(professional => professional.name === value);
                        setProfessionalId(selected?.id);
                    }}
                />

                <AutocompleteTextField
                    label={t(TK.byRoom)}
                    options={roomOptions}
                    value={roomId ?? undefined}
                    onValueChanged={setRoomId}
                />
            </FiltersGrid>

            {loading && (
                <LoadingContainer>
                    <Spinner size="sm" />
                </LoadingContainer>
            )}

            {!loading && error && (
                <Alert color="danger">{t(TK.errorLoadingData)}</Alert>
            )}

            {!loading && analytics && (
                <>
                    <SummaryCard>
                        <SummaryLabel>{t(TK.total)} {t(TK.surveyResponse).toLowerCase()}</SummaryLabel>
                        <SummaryCount>{analytics.totalResponses}</SummaryCount>
                    </SummaryCard>

                    {analytics.totalResponses === 0 && (
                        <Typography><i>{t(TK.noSurveyResponse)}</i></Typography>
                    )}

                    {!hasFieldResults && analytics.totalResponses > 0 && (
                        <Alert color="light">
                            {t(TK.surveySelectToInspect)}
                        </Alert>
                    )}

                    {(analytics.fields ?? []).map((field: SurveyAnalyticsFieldResult) => (
                        <FieldCardItem key={field.key} field={field} />
                    ))}

                    {(analytics.responses ?? []).length > 0 && (
                        <ResponsesSection>
                            <ResponsesSectionTitle>{t(TK.surveyIndividualResponses)}</ResponsesSectionTitle>
                            <ResponseList responses={analytics.responses ?? []} fieldLabels={responseFieldLabels} />
                        </ResponsesSection>
                    )}
                </>
            )}
        </Container>
    );
};
